"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const mobx_1 = require("mobx");
const react_toastify_1 = require("react-toastify");
const uuid_1 = require("uuid");
const ecommerce_1 = require("../../api/ecommerce");
const AuthorizationScopes_1 = __importDefault(require("../../common/js/enums/AuthorizationScopes"));
const noop_1 = require("../../common/js/helper/noop");
const translation_1 = require("../../common/js/helper/translation");
const AuthorizationStore_1 = __importDefault(require("../../common/js/stores/AuthorizationStore"));
const configuration_1 = __importDefault(require("../apis/configuration/shop/configuration"));
const CartErrorLevels_1 = __importDefault(require("../enums/CartErrorLevels"));
const CartErrors_1 = __importDefault(require("../enums/CartErrors"));
const Categories_1 = __importDefault(require("../enums/Categories"));
const product_1 = require("../helper/product");
const ShopwareProducts_1 = __importDefault(require("./ShopwareProducts"));
const UiStore_1 = __importDefault(require("./UiStore"));
class ShopwareCart {
    api = new ecommerce_1.CartApi(configuration_1.default);
    onRefreshDeferredPrices = null;
    loadingCounter = 0;
    internalCartLoading = false;
    internalCart = null;
    addProductsPromiseChain = Promise.resolve();
    constructor() {
        (0, mobx_1.makeObservable)(this, {
            loadingCounter: mobx_1.observable,
            internalCartLoading: mobx_1.observable,
            internalCart: mobx_1.observable,
            hasProductWithCategorySoftware: mobx_1.computed,
            showPrices: mobx_1.computed,
            productsCount: mobx_1.computed,
            shippingCosts: mobx_1.computed,
            isOrderOnAccountBlocked: mobx_1.computed,
            netPriceOnlyLineItems: mobx_1.computed,
            isLoading: mobx_1.computed,
            hasCart: mobx_1.computed,
            cart: mobx_1.computed,
            cartErrors: mobx_1.computed,
            appliedPromotionCode: mobx_1.computed,
            removeLineItemFromCart: mobx_1.action.bound,
            updateLineItemOfCart: mobx_1.action.bound,
            loadCart: mobx_1.action.bound,
            addProduct: mobx_1.action.bound,
            addProducts: mobx_1.action.bound,
            addLineItems: mobx_1.action.bound,
            reset: mobx_1.action.bound,
            setCart: mobx_1.action.bound,
        });
    }
    get hasProductWithCategorySoftware() {
        const currentLineItems = this.cart?.lineItems ?? [];
        const products = currentLineItems.map((lineItem) => {
            if (!lineItem.referencedId) {
                return;
            }
            return ShopwareProducts_1.default.getProduct(lineItem.referencedId);
        });
        return (products
            .flatMap((product) => product?.categories)
            .filter((category) => category?.extensions?.massiveCategory?.externalId === Categories_1.default.SOFTWARE)
            .length > 0);
    }
    get showPrices() {
        const currentLineItems = (this.cart?.lineItems ?? []).filter((lineItem) => lineItem.type === ecommerce_1.LineItemTypeEnum.Product);
        const items = currentLineItems.filter((lineItem) => {
            if (!lineItem.referencedId) {
                return false;
            }
            const product = ShopwareProducts_1.default.getProduct(lineItem.referencedId);
            if (!product) {
                return false;
            }
            return (0, product_1.showPrices)(product);
        });
        if (items.length > 0) {
            return true;
        }
        return (0, product_1.showPrices)();
    }
    get productsCount() {
        return this.cart && this.cart.lineItems
            ? this.cart.lineItems.filter((lineItem) => lineItem.type === ecommerce_1.LineItemTypeEnum.Product).length
            : 0;
    }
    get shippingCosts() {
        return (this.cart?.deliveries?.reduce((totalCost, delivery) => totalCost + delivery.shippingCosts.totalPrice, 0) ??
            0);
    }
    get isOrderOnAccountBlocked() {
        return !!this.cartErrors.find((error) => error.messageKey === CartErrors_1.default.orderOnAccount);
    }
    get netPriceOnlyLineItems() {
        const netPrice = this.cart?.price?.netPrice ?? 0;
        return netPrice - this.shippingCosts;
    }
    get isLoading() {
        return this.loadingCounter > 0;
    }
    get hasCart() {
        return !!this.internalCart;
    }
    get cart() {
        return this.internalCart;
    }
    get cartGroups() {
        return (this.cart?.lineItems.reduce((groups, lineItem) => {
            const lineItemGroup = lineItem.payload?.zkpCartItemGroup || ecommerce_1.LineItemPayloadZkpCartItemGroupEnum.Others;
            groups[lineItemGroup] = [...(groups[lineItemGroup] ?? []), lineItem];
            return groups;
        }, {}) ?? null);
    }
    get cartErrors() {
        if (!this.internalCart?.errors) {
            return [];
        }
        const errors = Array.isArray(this.internalCart?.errors)
            ? this.internalCart?.errors
            : Object.values(this.internalCart?.errors ?? {});
        /*
         * Shopware provides an associative array, if the Store API is used.
         * Therefore, we cannot define this parsing using the OpenAPI specification.
         */
        return errors
            .filter((error) => typeof error === 'object')
            .map((error) => ({
            messageKey: error?.messageKey ?? null,
            level: error?.level ?? null,
            parameters: error?.parameters ?? null,
        }));
    }
    get appliedPromotionCode() {
        const promotionLineItem = this.internalCart?.lineItems.find((lineItem) => lineItem.type === ecommerce_1.LineItemTypeEnum.Promotion && lineItem.referencedId);
        return promotionLineItem?.payload?.code ?? '';
    }
    removeLineItemFromCart(lineItemId) {
        const lineItem = this.cart?.lineItems.find((currentLineItem) => currentLineItem.id === lineItemId);
        if (!!lineItem?.payload?.massiveIsAdditionalSeats) {
            return this.updateLineItemOfCart(lineItemId, 0);
        }
        this.loadingCounter++;
        return this.api
            .deleteLineItems({
            deleteByIdsRequest: {
                ids: [lineItemId],
            },
        })
            .then((response) => {
            this.setCart(response ?? null);
            return this.updateProductInformationForLineItems();
        })
            .then(() => this.internalCart)
            .finally(() => {
            this.loadingCounter--;
        });
    }
    updateLineItemOfCart(lineItemId, quantity) {
        const lineItem = this.cart?.lineItems.find((currentLineItem) => currentLineItem.id === lineItemId);
        let lineItemToUpdate = {
            id: lineItemId,
            quantity: quantity,
        };
        if (!!lineItem?.payload?.massiveIsAdditionalSeats) {
            const softwareLineItem = this.cart?.lineItems.find((currentLineItem) => (currentLineItem?.payload?.massiveAdditionalSeatsCount ?? 0) > 0);
            if (!softwareLineItem) {
                return Promise.reject();
            }
            lineItemToUpdate = {
                id: softwareLineItem.id,
                quantity: 1,
                payload: {
                    massiveAdditionalSeatsCount: quantity,
                },
            };
        }
        this.loadingCounter++;
        return this.api
            .updateLineItems({
            lineItemRequest: {
                items: [lineItemToUpdate],
            },
        })
            .then((response) => {
            this.setCart(response ?? null);
            return this.updateProductInformationForLineItems();
        })
            .then(() => this.internalCart)
            .finally(() => {
            this.loadingCounter--;
        });
    }
    loadCart(afterCartLoaded, showErrors = true, portalArea) {
        if (!AuthorizationStore_1.default.hasScope(AuthorizationScopes_1.default.CART_READ) || this.internalCartLoading) {
            return Promise.resolve();
        }
        this.loadingCounter++;
        this.internalCartLoading = true;
        return this.api
            .readCart({ portalArea })
            .then((response) => {
            this.setCart(response ?? null);
            if (afterCartLoaded) {
                afterCartLoaded();
            }
            return this.updateProductInformationForLineItems();
        })
            .then(() => {
            if (this.cartErrors.length > 0 && showErrors) {
                this.cartErrors.forEach((error) => {
                    if (!error.messageKey) {
                        return;
                    }
                    const errorMessage = (0, translation_1.translateNoFallback)(`cart_errors.${error.messageKey.replace(/-/g, '_')}`);
                    switch (error.level) {
                        case CartErrorLevels_1.default.error:
                            react_toastify_1.toast.error(errorMessage);
                            break;
                        case CartErrorLevels_1.default.warning:
                            react_toastify_1.toast.warning(errorMessage);
                            break;
                        case CartErrorLevels_1.default.notice:
                            react_toastify_1.toast.info(errorMessage);
                            break;
                    }
                });
            }
            if (!this.onRefreshDeferredPrices) {
                this.onRefreshDeferredPrices = (0, mobx_1.reaction)(() => [UiStore_1.default.fiveSeconds], () => {
                    const defer = this.cartErrors.find((error) => error.messageKey === CartErrors_1.default.deferredProductPrice);
                    if (!defer || this.loadingCounter > 0) {
                        return;
                    }
                    this.loadCart(undefined, false);
                });
            }
        })
            .finally((0, mobx_1.action)(() => {
            this.internalCartLoading = false;
            this.loadingCounter--;
        }));
    }
    addProduct(id, quantity, additionalSeatsCount, machineId, uniqueId = false) {
        return this.addProducts([{ id, quantity, additionalSeatsCount, machineId }], uniqueId);
    }
    addProducts(products, uniqueId = false) {
        const items = products.map((product) => ({
            id: uniqueId ? (0, uuid_1.v4)().replace(/-/g, '') : product.id,
            referencedId: product.id,
            quantity: product.quantity,
            type: ecommerce_1.LineItemTypeEnum.Product,
            removable: true,
            stackable: true,
            payload: {
                massiveAdditionalSeatsCount: product.additionalSeatsCount ?? null,
                zkpMachineId: product.machineId ?? null,
            },
        }));
        this.loadingCounter++;
        return new Promise((resolve, reject) => {
            this.addProductsPromiseChain = this.addProductsPromiseChain.finally(() => {
                this.api
                    .addLineItems({
                    lineItemRequest: { items },
                })
                    .then((response) => {
                    this.setCart(response ?? null);
                    return this.updateProductInformationForLineItems();
                })
                    .then(() => this.internalCart)
                    .then(resolve, reject)
                    .finally(() => {
                    this.loadingCounter--;
                });
            });
        });
    }
    addLineItems(items) {
        this.loadingCounter++;
        return this.api
            .addLineItems({
            lineItemRequest: { items },
        })
            .then((response) => {
            this.setCart(response ?? null);
            return this.updateProductInformationForLineItems();
        })
            .then(() => this.internalCart)
            .finally(() => {
            this.loadingCounter--;
        });
    }
    reset() {
        this.internalCart = null;
    }
    setCart(cart) {
        this.internalCart = cart;
    }
    updateProductInformationForLineItems() {
        const defer = this.cartErrors.find((error) => error.messageKey === CartErrors_1.default.deferredProductPrice);
        const lineItems = this.cart?.lineItems ?? [];
        const productPromises = lineItems
            .filter((lineItem) => lineItem.type === ecommerce_1.LineItemTypeEnum.Product)
            .map((lineItem) => lineItem.referencedId ?? '')
            .filter((referencedId) => {
            if (!referencedId) {
                return false;
            }
            const product = ShopwareProducts_1.default.getProduct(referencedId);
            if (!product) {
                return true;
            }
            if (!product.extensions?.massiveDeferPriceDisplay?.defer) {
                return false;
            }
            // Reload deferred product data only, if the cart has resolved its deferred prices.
            return !defer;
        })
            .map((referenceId) => ShopwareProducts_1.default.loadProduct(referenceId));
        return Promise.all(productPromises).then(noop_1.noop);
    }
}
exports.default = new ShopwareCart();
